// WCAG contrast between two CSS colors (#rgb, #rrggbb or rgb()/rgba()); null when either can't be parsed.

function parseColor(color: any): number[] | null {
    const s = typeof color === 'string' ? color.trim().toLowerCase() : '';
    let m = s.match(/^#([0-9a-f]{3})$/);
    if (m) return m[1].split('').map((c) => { return parseInt(c + c, 16); });
    m = s.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/);
    if (m) return [m[1], m[2], m[3]].map((h) => { return parseInt(h, 16); });
    m = s.match(/^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})/);
    if (m) return [m[1], m[2], m[3]].map((n) => { return Math.min(255, Number(n)); });
    return null;
}

function luminance(rgb: number[]): number {
    const [r, g, b] = rgb.map((v) => {
        const c = v / 255;
        return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrastRatio(a: any, b: any): number | null {
    const ca = parseColor(a);
    const cb = parseColor(b);
    if (!ca || !cb) return null;
    const [hi, lo] = [luminance(ca), luminance(cb)].sort((x, y) => { return y - x; });
    return (hi + 0.05) / (lo + 0.05);
}

// Minimum contrast for the short, bold labels we put on team colors (WCAG large-text level).
const MIN_LABEL_CONTRAST = 3;

// Text color to use on top of `background`: white when it reads, otherwise the team's primary color,
// otherwise near-black. Keeps labels readable on light secondary colors (yellow, gold, white).
export function getReadableTextColor(background: any, primaryColor?: any): string {
    const candidates = ['#ffffff', primaryColor, '#171d28'];
    const readable = candidates.find((color) => {
        const ratio = contrastRatio(color, background);
        return ratio !== null && ratio >= MIN_LABEL_CONTRAST;
    });
    if (readable) return readable;
    const white = contrastRatio('#ffffff', background);
    return white === null || white >= (contrastRatio('#171d28', background) || 0) ? '#ffffff' : '#171d28';
}

// Below this, a color drawn as a thin line or ring on a white card is effectively invisible.
const MIN_ACCENT_ON_WHITE = 1.5;

// The team's secondary color (falls back to primary), the text color that reads on it, and `secondaryAccent`:
// the secondary for lines/rings on white cards, or the primary when the secondary is too pale to see there.
export function getSecondaryColors(colors: any): { secondary: string, onSecondary: string, secondaryAccent: string } {
    const primary = colors && colors.primaryColor;
    const secondary = (colors && typeof colors.secondaryColor === 'string' && colors.secondaryColor.trim().length > 0) ? colors.secondaryColor : primary;
    const onWhite = contrastRatio(secondary, '#ffffff');
    return {
        secondary,
        onSecondary: getReadableTextColor(secondary, primary),
        secondaryAccent: onWhite !== null && onWhite < MIN_ACCENT_ON_WHITE && primary ? primary : secondary,
    };
}

// Accent color for text on a white card (hover headlines, links): the secondary color when it reads on white,
// otherwise the primary, otherwise near-black. Never puts a light color (white, pale yellow) on a light card.
export function getAccentTextOnWhite(colors: any): string {
    const primary = colors && typeof colors.primaryColor === 'string' && colors.primaryColor.trim().length > 0 ? colors.primaryColor : '';
    const secondary = colors && typeof colors.secondaryColor === 'string' && colors.secondaryColor.trim().length > 0 ? colors.secondaryColor : '';
    const readable = [secondary, primary].find((color) => {
        const ratio = color ? contrastRatio(color, '#ffffff') : null;
        return ratio !== null && ratio >= MIN_LABEL_CONTRAST;
    });
    return readable || '#171d28';
}

// The even mix of two colors as rgb(), e.g. the middle of a primary-to-secondary gradient; null when either can't be
// parsed.
export function mixColors(a: any, b: any, weight = 0.5): string | null {
    const ca = parseColor(a);
    const cb = parseColor(b);
    if (!ca || !cb) return null;
    const [r, g, bl] = ca.map((value, index) => { return Math.round(value * (1 - weight) + cb[index] * weight); });
    return `rgb(${r}, ${g}, ${bl})`;
}

// True when white titles read on `background` (otherwise draw them dark). Unparseable colors keep white titles.
export function isWhiteTextReadable(background: any): boolean {
    const ratio = contrastRatio('#ffffff', background);
    return ratio === null || ratio >= MIN_LABEL_CONTRAST;
}
